/**
 * Корень приложения.
 *
 * Навигация — простое состояние вместо роутера: разделов немного, а URL внутри
 * десктоп-оболочки не является частью пользовательского опыта (Telegram —
 * системный раздел, а не страница, §18 ТЗ). Роутер добавит зависимость и
 * соблазн адресовать разделы ссылками, чего мы как раз избегаем.
 *
 * Набор разделов зависит от прав (§26 ТЗ). Напоминание: это UX. Прямой запрос к
 * скрытому разделу всё равно упрётся в 403 на сервере.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { SessionProvider, useSession } from "./state/session.tsx";
import { LoginView } from "./views/LoginView.tsx";
import { InstallHint, OfflineBanner } from "./components/PwaBanners.tsx";
import { WorkspaceView } from "./views/WorkspaceView.tsx";
import { SettingsView } from "./views/SettingsView.tsx";
import { SectionView } from "./views/SectionView.tsx";
import { AuditView } from "./views/AuditView.tsx";
import { StaysView } from "./views/StaysView.tsx";
import { desktop, isDesktop } from "./lib/desktop.js";
import {
  BellIcon,
  BellOffIcon,
  LogoutIcon,
  SidebarIcon,
} from "./components/icons.tsx";
import { BucorMark } from "./components/BucorMark.tsx";
import {
  notificationState,
  toggleNotifications,
  type NotificationState,
} from "./lib/notifications.js";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { SectionDto } from "./lib/api.js";
import { SECTIONS_CHANGED } from "./lib/sectionsEvent.js";
import { OPEN_EXTERNAL_URL } from "./lib/externalLink.js";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { useIsMobile } from "./lib/useMedia.ts";
import { disablePush, enablePush, pushState, PUSH_LABELS, type PushState } from "./lib/push.ts";
import { requestOpenChat } from "./lib/deepLink.ts";
import { isStandalone } from "./lib/pwa.ts";
import { MobileBottomBar, MobileHeader, PushPrompt, type MobileNavItem } from "./components/MobileShell.tsx";

/**
 * Системные разделы. Живут в доверенном renderer'е, знают про роли и права,
 * удалению не подлежат. Разделы из базы (CRM, WhatsApp, браузер) сюда не
 * входят: они внешние, и система не знает о них ничего, кроме адреса.
 */
type SystemSection = "telegram" | "stays" | "settings" | "audit";

/** Что открыто: системный раздел либо внешний по идентификатору. */
type Active =
  { kind: "system"; key: SystemSection } | { kind: "external"; id: string };

const SIDEBAR_KEY = "bucor.sidebar";
const DETAILS_KEY = "bucor.details";

const NOTIFICATION_LABELS: Record<NotificationState, string> = {
  on: "Оповещения включены",
  off: "Включить оповещения о сообщениях",
  // Отказ отменяется только в настройках браузера — честнее сказать это
  // сразу, чем показывать кнопку, которая молча ничего не делает.
  denied: "Оповещения запрещены в настройках браузера",
  unsupported: "Оповещения недоступны в этой среде",
};

export default function App() {
  return (
    /*
     * TooltipProvider обязателен и ставится один раз на всё дерево.
     *
     * Компоненты Tooltip из shadcn читают контекст провайдера и без него
     * бросают исключение при первой же отрисовке. Ошибка коварна тем, что
     * проявляется не там, где её ждёшь: экран входа подсказок не содержит и
     * работает, а весь воркспейс падает в белый экран.
     */
    <TooltipProvider delayDuration={200}>
      <SessionProvider>
        <Shell />
      </SessionProvider>
      <OfflineBanner />
      <InstallHint />
    </TooltipProvider>
  );
}

function Shell() {
  const { user, loading, logout, can, api } = useSession();
  const mobile = useIsMobile();
  const [active, setActive] = useState<Active>({
    kind: "system",
    key: "telegram",
  });

  /*
   * Внешние разделы приходят с сервера и зависят от выданного доступа.
   *
   * Пустой список — обычное состояние, а не ошибка: администратор мог ещё
   * ничего не завести или не выдать доступ этому сотруднику.
   */
  const [external, setExternal] = useState<SectionDto[]>([]);

  /*
   * Сообщаем оболочке, кто вошёл.
   *
   * От этого зависит profile браузера: у каждого сотрудника собственное
   * хранилище cookie и сессий. До этого профиль был один на всех и назывался
   * anonymous — вход второго сотрудника в CRM выбивал оттуда первого, и это
   * выглядело не как ошибка, а как «опять разлогинило».
   *
   * Вызов идемпотентен: оболочка сравнивает partition и на том же профиле не
   * делает ничего. Иначе каждая перерисовка закрывала бы открытые вкладки.
   */
  useEffect(() => {
    if (!user) return;
    desktop()?.setProfile(user.id);
  }, [user]);

  /**
   * Нет сессии — вкладки внешних разделов обязаны исчезнуть.
   *
   * ── Что чинится ─────────────────────────────────────────────────────────
   *
   * Вкладка CRM или WhatsApp — это отдельная поверхность оболочки, лежащая
   * ПОВЕРХ окна. Она не часть React-дерева, и сама по себе при перерисовке
   * никуда не девается.
   *
   * Пряталась она ровно в одном месте — в очистке эффекта SectionView, то
   * есть при его штатном размонтировании. Когда сессия обрывается прямо
   * внутри раздела, приложение переключается на экран входа, и рассчитывать
   * на очистку в этот момент нельзя: достаточно ошибки в дереве или
   * прерванной перерисовки, чтобы она не выполнилась. Результат — форма
   * входа под чужой страницей, и войти обратно физически нечем.
   *
   * Поэтому проверка стоит здесь и опирается только на факт отсутствия
   * пользователя, а не на жизненный цикл компонента.
   */
  /*
   * Сброс профиля оболочки — только на настоящем выходе: был сотрудник, стал
   * никто. При запуске user тоже null, пока /me не ответил, и сброс там
   * закрывал бы вкладки и забывал пароли просто от перезапуска окна.
   */
  const hadUser = useRef(false);
  useEffect(() => {
    if (user) {
      hadUser.current = true;
      return;
    }
    desktop()?.hideTabs();
    if (hadUser.current) {
      hadUser.current = false;
      desktop()?.resetProfile?.();
    }
  }, [user]);

  /**
   * Адрес, открытый по ссылке из переписки.
   *
   * Живёт до следующей ссылки или до ухода из раздела: возвращаться в
   * «Браузер» и видеть страницу, открытую час назад из чужого чата, странно.
   */
  const [browserUrl, setBrowserUrl] = useState<string | null>(null);

  /*
   * Ссылка из переписки открывается в разделе «Браузер».
   *
   * Если раздела нет — а его может не быть, доступ выдаёт администратор, —
   * событие не отменяем, и отправитель откроет ссылку по-своему. Молча
   * проглотить нажатие нельзя: для человека это выглядит как сломанная ссылка.
   */
  useEffect(() => {
    const onOpen = (event: Event): void => {
      const url = (event as CustomEvent<string>).detail;
      const browser = external.find((section) => section.kind === "BROWSER");
      if (!url || !browser) return;

      event.preventDefault();
      setBrowserUrl(url);
      setActive({ kind: "external", id: browser.id });
    };

    window.addEventListener(OPEN_EXTERNAL_URL, onOpen);
    return () => window.removeEventListener(OPEN_EXTERNAL_URL, onOpen);
  }, [external]);

  // Уход из внешних разделов забывает пришедшую ссылку: вернувшись в
  // «Браузер», человек ждёт свой раздел, а не страницу из чужого чата.
  useEffect(() => {
    if (active.kind !== "external") setBrowserUrl(null);
  }, [active]);

  const reloadSections = useCallback(() => {
    let cancelled = false;
    api
      .sections()
      .then(({ items }) => {
        if (!cancelled) setExternal(items);
      })
      .catch(() => {
        // Недоступный список разделов не должен ломать вход: системные
        // разделы работают и без него.
        if (!cancelled) setExternal([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  /*
   * Смена сотрудника за тем же окном сбрасывает всё, что принадлежало
   * прошлому: его вкладки внешних разделов, открытый раздел и ссылку из его
   * переписки. Иначе до ответа /sections следующий видел чужие вкладки и
   * мог оказаться прямо внутри раздела, к которому у него нет доступа.
   */
  const userId = user?.id ?? null;
  useEffect(() => {
    setExternal([]);
    setActive({ kind: "system", key: "telegram" });
    setBrowserUrl(null);
  }, [userId]);

  useEffect(() => {
    if (!user) return;
    return reloadSections();
  }, [reloadSections, user]);

  /*
   * Каким рабочим системам разрешены оповещения.
   *
   * ── Зачем ───────────────────────────────────────────────────────────────
   *
   * Оповещения CRM и WhatsApp раньше не доходили вовсе: оболочка отказывала
   * всем страницам подряд, и о новом сообщении сотрудник узнавал, только
   * заглянув во вкладку. Теперь решает личная настройка каждого.
   *
   * ── Почему список целиком ───────────────────────────────────────────────
   *
   * Отправляем полный набор разрешённых адресов, а не «включи вот этот».
   * Состояние оболочки при этом не может разойтись с настройками: что бы ни
   * потерялось по дороге, следующая отправка всё выправит.
   *
   * Отсутствие записи означает «включено»: строка в базе заводится только
   * когда сотрудник изменил умолчание.
   */
  useEffect(() => {
    const shell = desktop();
    if (!user || !shell?.setNotificationOrigins) return;

    let cancelled = false;

    void api
      .sectionNotifications()
      .then(({ items }) => {
        if (cancelled) return;

        const disabled = new Set(
          items.filter((item) => !item.enabled).map((item) => item.sectionId),
        );

        shell.setNotificationOrigins?.(
          external
            .filter((section) => section.url && !disabled.has(section.id))
            .map((section) => section.url as string),
        );
      })
      .catch(() => {
        /* настройки недоступны — оповещения молчат, а не сыплются */
      });

    return () => {
      cancelled = true;
    };
  }, [api, external, user]);

  // Администратор завёл или удалил раздел — вкладки обязаны обновиться сразу,
  // а не после перезагрузки страницы.
  useEffect(() => {
    const handler = (): void => void reloadSections();
    window.addEventListener(SECTIONS_CHANGED, handler);
    return () => window.removeEventListener(SECTIONS_CHANGED, handler);
  }, [reloadSections]);

  /*
   * Свёрнутость списка чатов живёт здесь, а не в WorkspaceView.
   *
   * Переключатель обязан быть виден в ОБОИХ положениях. Первая версия ставила
   * его язычком у кромки самой колонки — свернуть получалось, а развернуть
   * было нечем: вместе с колонкой исчезала и кнопка. Шапка есть всегда.
   *
   * Хранится в localStorage: это предпочтение рабочего места, а не
   * пользователя, и на сервере ему делать нечего. Чтение обёрнуто в try —
   * при недоступном хранилище лучше открытая колонка, чем белый экран.
   */
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_KEY) !== "collapsed";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDEBAR_KEY,
        sidebarOpen ? "open" : "collapsed",
      );
    } catch {
      // Недоступное хранилище — не причина ломать экран.
    }
  }, [sidebarOpen]);

  /**
   * Карточка чата справа.
   *
   * Та же логика, что и у левой колонки: 300 пикселей — заметная доля экрана,
   * и при чтении длинной переписки они нужнее самой переписке. Состояние
   * хранится отдельным ключом: колонки закрывают по разным поводам, и общий
   * ключ прятал бы обе разом.
   */
  const [detailsOpen, setDetailsOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(DETAILS_KEY) !== "collapsed";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(
        DETAILS_KEY,
        detailsOpen ? "open" : "collapsed",
      );
    } catch {
      // См. выше.
    }
  }, [detailsOpen]);

  /*
   * Оповещения о новых сообщениях.
   *
   * Разрешение запрашивается только по нажатию на колокольчик и никогда при
   * загрузке: незапрошенный вопрос браузеры часто игнорируют, а человек,
   * которого спросили не вовремя, жмёт «запретить» — и вернуть это можно
   * только через настройки браузера, то есть практически никогда.
   */
  const [notifications, setNotifications] = useState<NotificationState>(() =>
    notificationState(),
  );

  const switchNotifications = async (): Promise<void> => {
    setNotifications(await toggleNotifications());
  };

  /*
   * Телефон: колокольчик — пуш-уведомления ЭТОГО устройства (docs/PWA.md),
   * а не оповещения открытой вкладки: на телефоне вкладка почти всегда
   * свёрнута, и толку от них мало.
   */
  const [push, setPush] = useState<PushState>("unsupported");
  useEffect(() => {
    if (!mobile || !user) return;
    void pushState().then(setPush);
  }, [mobile, user]);
  const togglePush = async (): Promise<void> => {
    if (push === "on") {
      await disablePush(api);
      setPush("off");
      return;
    }
    if (push === "needs-install") {
      window.alert(
        "На iPhone уведомления приходят только в установленном приложении: Safari → «Поделиться» → «На экран „Домой“», затем откройте BUCOR со значка.",
      );
      return;
    }
    if (push === "denied") {
      window.alert("Уведомления для BUCOR запрещены в настройках телефона — включите их там.");
      return;
    }
    try {
      setPush(await enablePush(api));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Не удалось включить уведомления");
    }
  };

  /*
   * Переход по пушу: адрес `/?chat=…` или `/?section=…` — при запуске из
   * уведомления и сообщением воркера, если приложение уже открыто.
   */
  useEffect(() => {
    const go = (raw: string) => {
      const url = new URL(raw, window.location.origin);
      const chat = url.searchParams.get("chat");
      const section = url.searchParams.get("section");
      if (chat) {
        setActive({ kind: "system", key: "telegram" });
        requestOpenChat(chat);
      } else if (section === "telegram" || section === "stays" || section === "settings" || section === "audit") {
        setActive({ kind: "system", key: section });
      }
    };
    // Только когда сотрудник уже известен: смена сотрудника сбрасывает
    // раздел на Telegram (эффект выше), и ранний переход он бы перебил.
    if (!userId) return;
    if (window.location.search) {
      go(window.location.href);
      window.history.replaceState(window.history.state, "", "/");
    }
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === "tw:navigate" && typeof e.data.url === "string") go(e.data.url);
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
  }, [userId]);

  if (loading) {
    return (
      <div className="grid h-screen place-items-center text-sm text-muted-foreground">
        Загрузка…
      </div>
    );
  }

  if (!user) {
    return <LoginView />;
  }

  // Разделы собираются из прав, а не из роли: добавление роли не потребует
  // править этот список.
  const systemSections: Array<{
    key: SystemSection;
    label: string;
    visible: boolean;
  }> = [
    { key: "telegram", label: "Telegram", visible: true },
    {
      key: "stays",
      label: "Апартаменты",
      visible: can("stays.view"),
    },
    {
      key: "settings",
      label: "Настройки",
      visible:
        can("user.view") ||
        can("account.assign.manager") ||
        can("section.manage"),
    },
    {
      key: "audit",
      label: "Аудит",
      visible: can("audit.view") || can("audit.view.scoped"),
    },
  ];

  const visible = systemSections.filter((s) => s.visible);

  /*
   * Нормализация выбора.
   *
   * Раздел мог исчезнуть под ногами: администратор отозвал доступ, выключил
   * раздел или у пользователя сменилась роль. Держать открытым то, чего
   * больше нет, нельзя — возвращаемся к Telegram, который доступен всем.
   */
  const chosenExternal =
    active.kind === "external"
      ? (external.find((x) => x.id === active.id) ?? null)
      : null;

  /*
   * Раздел мог исчезнуть под ногами: администратор отозвал доступ, выключил
   * раздел или у пользователя сменилась роль.
   *
   * Подмена ВЫЧИСЛЯЕТСЯ, а не пишется в состояние. Эффект тут был бы не
   * только лишней перерисовкой: его пришлось бы объявлять после ранних
   * возвратов по loading и user, а хук в такой позиции нарушает порядок
   * вызовов и ломается ровно тогда, когда состояние меняется.
   */
  const alive =
    active.kind === "system"
      ? visible.some((s) => s.key === active.key)
      : chosenExternal !== null;

  const shown: Active = alive ? active : { kind: "system", key: "telegram" };

  const isTelegram = shown.kind === "system" && shown.key === "telegram";
  const openedExternal = shown.kind === "external" ? chosenExternal : null;

  const isActive = (target: Active): boolean =>
    target.kind === "system"
      ? shown.kind === "system" && shown.key === target.key
      : shown.kind === "external" && shown.id === target.id;

  /*
   * Содержимое раздела — одно на обе раскладки: на телефоне меняется только
   * обвязка (шапка, нижняя панель), а не сами разделы.
   */
  const content = (
    <ErrorBoundary resetKey={shown.kind === "system" ? shown.key : shown.id}>
      {shown.kind === "system" && shown.key === "telegram" && (
        <WorkspaceView sidebarOpen={sidebarOpen} detailsOpen={detailsOpen} />
      )}
      {shown.kind === "system" && shown.key === "settings" && <SettingsView />}
      {shown.kind === "system" && shown.key === "audit" && <AuditView />}
      {shown.kind === "system" && shown.key === "stays" && <StaysView />}
      {openedExternal && (
        <SectionView
          section={openedExternal}
          overrideUrl={openedExternal.kind === "BROWSER" ? browserUrl : null}
        />
      )}
    </ErrorBoundary>
  );

  if (mobile) {
    const items: MobileNavItem[] = [
      ...visible.map((s) => ({
        id: `sys:${s.key}`,
        label: s.label,
        icon: s.key,
        active: isActive({ kind: "system", key: s.key }),
        onSelect: () => setActive({ kind: "system", key: s.key }),
      })),
      ...external.map((x) => ({
        id: `ext:${x.id}`,
        label: x.title,
        icon: "external" as const,
        active: isActive({ kind: "external", id: x.id }),
        onSelect: () => setActive({ kind: "external", id: x.id }),
      })),
    ];
    const current = items.find((i) => i.active);
    return (
      /*
       * Телефон: шапка сверху, раздел на всю ширину, разделы — внизу под
       * большим пальцем (components/MobileShell.tsx).
       */
      <div className="flex h-dvh flex-col [background:var(--app-bg)]">
        <MobileHeader
          title={current?.label ?? "BUCOR"}
          user={{ name: user.displayName, role: ROLE_LABELS[user.role] ?? user.role }}
          notifications={
            push === "unsupported"
              ? null
              : {
                  on: push === "on",
                  disabled: false,
                  label: PUSH_LABELS[push],
                  onToggle: () => void togglePush(),
                }
          }
          onTestPush={push === "on" ? () => void api.pushTest() : undefined}
          onLogout={() => void logout()}
        />
        {isStandalone() && push === "off" && <PushPrompt onEnable={() => void togglePush()} />}
        <main
          key={shown.kind === "system" ? shown.key : shown.id}
          className="view-enter min-h-0 flex-1"
        >
          {content}
        </main>
        <MobileBottomBar items={items} />
      </div>
    );
  }

  return (
    /*
     * Оболочка построена как ПОДЛОЖКА с парящими панелями, а не как сплошная
     * плита, разрезанная линиями в один пиксель.
     *
     * Разница не декоративная. Разделительная линия говорит «здесь кончается
     * одна область и начинается другая», и только. Зазор с тенью говорит, что
     * это отдельные поверхности с собственной прокруткой и собственным
     * содержимым, — а в трёхколоночном интерфейсе это первое, что нужно
     * понять с одного взгляда.
     */
    <div className="flex h-dvh flex-col [background:var(--app-bg)]">
      {/*
        Полоса приложения отделена волосяной линией.
        Граница стояла прозрачной, то есть её не было вовсе: шапка сливалась с
        холстом, и на экране не читалось, где кончается обвязка приложения и
        начинается рабочая область. В трёхколоночном интерфейсе это первое,
        что нужно понять с одного взгляда.
      */}
      <header className="flex h-14 flex-none items-center gap-5 border-b border-white/40 bg-white/40 px-5 backdrop-blur-xl dark:border-white/5 dark:bg-white/[0.03]">
        <div className="flex flex-none items-center gap-2.5">
          {/* Утверждённый знак вместо буквы-заглушки. Подложка ему не нужна:
              он сам держит форму, а квадрат под квадратами дал бы вторую
              рамку там, где её нет в оригинале. */}
          <BucorMark className="size-7 flex-none" />
          <span className="text-[15px] font-semibold tracking-[-0.019em] whitespace-nowrap text-heading">
            BUCOR{" "}
            <span className="font-medium text-muted-foreground max-[860px]:hidden">
              WORKSPACE
            </span>
          </span>
        </div>

        {/*
          Переключатель списка чатов. Показывается только в разделе Telegram —
          в управлении и аудите левой колонки нет, и кнопка, которая ничего не
          делает, хуже отсутствующей.
        */}
        {isTelegram && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setSidebarOpen((v) => !v)}
                aria-pressed={sidebarOpen}
                aria-label={
                  sidebarOpen ? "Скрыть список чатов" : "Показать список чатов"
                }
                className={cn(
                  "flex-none",
                  sidebarOpen ? "text-brand" : "text-muted-foreground",
                )}
              >
                <SidebarIcon className="size-[18px]" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {sidebarOpen ? "Скрыть список чатов" : "Показать список чатов"}
            </TooltipContent>
          </Tooltip>
        )}

        {isTelegram && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDetailsOpen((v) => !v)}
                aria-pressed={detailsOpen}
                aria-label={
                  detailsOpen
                    ? "Скрыть карточку чата"
                    : "Показать карточку чата"
                }
                className={cn(
                  "flex-none",
                  detailsOpen ? "text-brand" : "text-muted-foreground",
                )}
              >
                {/* Зеркальная иконка: тот же знак колонки, но справа — иначе
                    две одинаковые кнопки рядом неразличимы. */}
                <SidebarIcon className="size-[18px] -scale-x-100" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {detailsOpen ? "Скрыть карточку чата" : "Показать карточку чата"}
            </TooltipContent>
          </Tooltip>
        )}

        {isTelegram && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => void switchNotifications()}
                aria-pressed={notifications === "on"}
                disabled={
                  notifications === "unsupported" || notifications === "denied"
                }
                aria-label={NOTIFICATION_LABELS[notifications]}
                className={cn(
                  "flex-none",
                  notifications === "on"
                    ? "text-brand"
                    : "text-muted-foreground",
                )}
              >
                {notifications === "on" ? (
                  <BellIcon className="size-[18px]" />
                ) : (
                  <BellOffIcon className="size-[18px]" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {NOTIFICATION_LABELS[notifications]}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Сегментированный переключатель: разделов мало и они равнозначны,
            выпадающий список тут был бы лишним кликом. */}
        <nav className="flex min-w-0 gap-1 overflow-x-auto rounded-full bg-muted p-1">
          {[
            ...visible.map((s) => ({
              id: `sys:${s.key}`,
              label: s.label,
              target: { kind: "system", key: s.key } as Active,
            })),
            /*
             * Внешние разделы идут ПОСЛЕ системных и в порядке, заданном
             * администратором. Порядок неслучаен: Telegram — основная работа,
             * и он не должен уезжать вправо от того, что добавили позже.
             */
            ...external.map((x) => ({
              id: `ext:${x.id}`,
              label: x.title,
              target: { kind: "external", id: x.id } as Active,
            })),
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(item.target)}
              className={cn(
                /*
                 * border-transparent и bg-transparent здесь обязательны.
                 * Базовое правило слоя base красит ЛЮБУЮ кнопку без класса
                 * фона: неактивные пункты получали белую заливку с обводкой и
                 * выглядели как равноправные таблетки — выделение активного
                 * раздела пропадало.
                 */
                "flex-none rounded-full border border-transparent bg-transparent px-4 py-1.5 text-sm font-medium transition-all",
                isActive(item.target)
                  ? "bg-primary text-primary-foreground shadow-[0_2px_10px_-4px_var(--accent-fill)]"
                  : "text-muted-foreground hover:bg-card/70 hover:text-brand",
              )}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {/* В браузере полезно понимать, что часть возможностей оболочки
              недоступна. */}
          {!isDesktop() && (
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              браузер
            </span>
          )}

          <div className="flex flex-col items-end leading-tight">
            <span className="text-sm font-medium">{user.displayName}</span>
            <span className="text-xs text-muted-foreground">
              {ROLE_LABELS[user.role] ?? user.role}
            </span>
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void logout()}
                aria-label="Выйти"
              >
                <LogoutIcon className="size-[18px]" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Выйти</TooltipContent>
          </Tooltip>
        </div>
      </header>

      {/* key на разделе: без него React переиспользует узел, и анимация
          появления не срабатывает при переключении вкладок. */}
      <main
        key={shown.kind === "system" ? shown.key : shown.id}
        className="view-enter min-h-0 flex-1 px-3 pb-3"
      >
        {/* Граница внутри main, а не вокруг него: шапка обязана пережить
            падение раздела, иначе уйти с упавшего экрана будет некуда. */}
        {content}
      </main>
    </div>
  );
}

const ROLE_LABELS: Record<string, string> = {
  MANAGER_INTERN: "Стажёр менеджера",
  MANAGER: "Менеджер",
  SENIOR_MANAGER: "Старший менеджер",

  OPERATOR_INTERN: "Стажёр оператора",
  OPERATOR: "Оператор",
  SENIOR_OPERATOR: "Старший оператор",

  HR: "HR",
  ADMIN: "Администратор",
};
